/**
 * Deterministic daily seed. Hash of the local date string `YYYY-MM-DD`
 * via a stable 32-bit mix (xmur3) so two devices on the same day land on
 * the same ring path.
 *
 * Daily runs are local-only (no /api/runs submission) because the server
 * issues its own seed for ranked runs — a client-overridden seed would
 * not match the server's replay context. Daily PBs are kept under a
 * separate localStorage key from the unranked PB system.
 */

const DAILY_PB_KEY = "aether.daily_pbs";

export function todayKey(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function dailySeed(key = todayKey()): number {
  // xmur3 — fast, stable 32-bit hash. Output range fits the existing
  // Math.floor(Math.random() * 0x7fffffff) seed contract in game.ts.
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  }
  return (h >>> 1) & 0x7fffffff;
}

interface DailyPbsMap { [date: string]: number }

export function recordDailyBest(score: number, key = todayKey()): boolean {
  try {
    const map = JSON.parse(localStorage.getItem(DAILY_PB_KEY) || "{}") as DailyPbsMap;
    const prev = map[key] ?? 0;
    if (score > prev) {
      map[key] = score;
      localStorage.setItem(DAILY_PB_KEY, JSON.stringify(map));
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function getDailyBest(key = todayKey()): number {
  try {
    const map = JSON.parse(localStorage.getItem(DAILY_PB_KEY) || "{}") as DailyPbsMap;
    return map[key] ?? 0;
  } catch {
    return 0;
  }
}

const STREAK_KEY = "aether.daily_streak";
interface DailyStreak { lastDate: string; count: number }

/**
 * Bump the consecutive-days played counter. Idempotent within a day —
 * calling repeatedly only counts once per calendar day.
 * Returns the new count (so the caller can display it without re-reading).
 */
export function bumpDailyStreak(today = todayKey()): number {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    const cur: DailyStreak = raw ? JSON.parse(raw) : { lastDate: "", count: 0 };
    if (cur.lastDate === today) return cur.count;
    // Was yesterday? Continue the streak. Otherwise restart.
    const yesterday = yesterdayKey(today);
    cur.count = cur.lastDate === yesterday ? cur.count + 1 : 1;
    cur.lastDate = today;
    localStorage.setItem(STREAK_KEY, JSON.stringify(cur));
    return cur.count;
  } catch {
    return 0;
  }
}

export function getDailyStreak(): number {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return 0;
    const cur: DailyStreak = JSON.parse(raw);
    // If they missed yesterday, the streak is effectively broken — surface 0
    // so the UI doesn't lie about "currently on a streak".
    const today = todayKey();
    if (cur.lastDate !== today && cur.lastDate !== yesterdayKey(today)) return 0;
    return cur.count;
  } catch {
    return 0;
  }
}

function yesterdayKey(today: string): string {
  const d = new Date(today + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  return todayKey(d);
}
